import { action, computed } from "@ember/object"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Planbook } from "@shared/models/planbook"
import { isApple } from "@shared/unified-tiptap/util/is-apple"
import copyPlanbookLinkAction from "cc-frontend/lib/actions/app-state/COPY_PLANBOOK_LINK"
import { createQrCode, createShareLink } from "cc-frontend/lib/share-link"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import type QRCodeStyling from "qr-code-styling"
import styles from "./link-sharing.module.css"

interface Sig {
  Args: {
    planbook: Planbook
  }
}

export default class LinkSharing extends Component<Sig> {
  readonly styles = styles

  @service declare modalManager: ModalManager
  @service declare rpc: Rpc
  @service declare dispatcher: Dispatcher
  @service declare session: Session

  @service notifier

  readonly shareLink: string
  readonly qr: QRCodeStyling

  @tracked copyLinkButtonText = "Copy Link"
  @tracked isChangingPublicPermission = false

  constructor(owner: unknown, args: Sig["Args"]) {
    super(owner, args)

    this.shareLink = createShareLink(this.pathname, new URLSearchParams(window.location.search))
    this.qr = createQrCode(this.shareLink)
  }

  get pathname() {
    return window.location.pathname.replace(/\/sharing$/, "/planner/week")
  }

  @computed("args.planbook.attributes.publicAccess.roles.[]")
  get planbookSharedWithWorld() {
    return this.args.planbook.attributes.publicAccess?.roles?.[0] === "planbook:is-viewer"
  }

  get hostName() {
    // @ts-ignore
    return typeof window === "undefined" && typeof window.location === "undefined"
      ? "https://www.commonplanner.com"
      : window.location.host
  }

  @action
  changePublicPermission() {
    const role = this.planbookSharedWithWorld ? ("NONE" as const) : ("VIEWER" as const)
    this.isChangingPublicPermission = true
    this.rpc
      .planbook_collaboration_change_public_permission({
        planbookId: this.args.planbook.id,
        role: role,
      })
      .finally(() => {
        this.isChangingPublicPermission = false
      })
  }

  @action
  async insertQrCode(element: HTMLImageElement) {
    // Convert to blog src for an img tag. Chrome gives this more copy/save options
    // than a plain canvas or SVG.
    const asBlob = (await this.qr.getRawData("svg")) as Blob
    const url = URL.createObjectURL(asBlob)
    element.src = url
  }

  @action
  emailLink() {
    setTimeout(() => {
      this.modalManager.openModal("planner/menus/parts/email-link-form", {
        pathname: this.pathname,
        baseUrlParams: new URLSearchParams(window.location.search),
        hideNav: false,
        dateString: null,
        courseTitle: null,
        planbook: this.args.planbook,
        user: this.session.user.content,
      })
    })
  }

  @action
  showCopyToast() {
    this.copyLinkButtonText = "Copied!"
    later(() => {
      if (this.isDestroyed || this.isDestroying) return
      this.copyLinkButtonText = "Copy URL"
    }, 2000)

    this.notifier.success("", {
      title: `Your URL has been Copied!`,
      message: `Press ${isApple ? "Command(⌘) + v" : "Control + v"} to paste.`,
      duration: 5000,
    })

    this.trackAnalytics("link")
  }

  @action
  downloadQrCode() {
    this.qr.download({
      name: `${this.args.planbook.attributes.title || "Common Planner"} QR Code`,
      // SVG would be nicer, but are those easy to open on Windows?
      extension: "png",
    })

    this.trackAnalytics("qr_code")
  }

  private trackAnalytics(copied: "link" | "qr_code") {
    if (this.session.user.content) {
      this.dispatcher.dispatchPreparedAction(
        copyPlanbookLinkAction({
          userId: this.session.user.content.id,
          planbookId: this.args.planbook.id,
          planbookTitle: this.args.planbook.attributes.title,
          perspective: "sharing",
          copied,
          url: this.shareLink,
          shareScope: "planbook",
        })
      )
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Planbook::Collaborations::LinkSharing": LinkSharing
  }
}
